import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { localeConfig } from '../i18n';

const normalizePath = (path) => {
  if (!path || path === '/') {
    return '/';
  }

  const cleaned = path.replace(/\/+/g, '/');
  if (cleaned.endsWith('/') && cleaned.length > 1) {
    return cleaned.replace(/\/$/, '');
  }

  return cleaned;
};

const stripLocale = (path) => {
  const normalized = normalizePath(path);
  
  // Проверяем каждую доступную локаль
  for (const locale of localeConfig.availableLocales) {
    if (locale === localeConfig.defaultLocale) continue; // Пропускаем язык по умолчанию
    
    if (normalized === `/${locale}`) {
      return '/';
    }

    if (normalized.startsWith(`/${locale}/`)) {
      const rest = normalized.slice(locale.length + 1);
      return rest ? `/${rest}` : '/';
    }

    if (normalized.endsWith(`/${locale}`)) {
      const base = normalized.slice(0, -(locale.length + 1));
      return base === '' ? '/' : base;
    }
  }

  return normalized;
};

const applyLocale = (basePath, lng) => {
  const normalized = stripLocale(basePath);

  // Если это язык по умолчанию, не добавляем префикс
  if (lng === localeConfig.defaultLocale) {
    return normalized === '/' ? '/' : normalized;
  }

  // Для других языков добавляем префикс
  if (normalized === '/') {
    return `/${lng}`;
  }
  return `${normalized}/${lng}`;
};

const LanguageSwitcher = ({ className = '' }) => {
  useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Не показываем переключатель, если это отключено в настройках
  if (!localeConfig.showLanguageSwitcher) {
    return null;
  }

  // Не показываем переключатель, если доступен только один язык
  if (localeConfig.availableLocales.length <= 1) {
    return null;
  }

  const changeLanguage = (lng) => {
    const basePath = stripLocale(location.pathname);
    const targetPath = applyLocale(basePath, lng);
    const destination = `${targetPath}${location.search}${location.hash}`;

    if (destination !== `${location.pathname}${location.search}${location.hash}`) {
      navigate(destination);
    }
  };

  const currentPath = normalizePath(location.pathname);
  
  // Определяем текущий язык динамически на основе доступных локалей
  const getCurrentLang = () => {
    // Проверяем каждую доступную локаль
    for (const locale of localeConfig.availableLocales) {
      if (locale === localeConfig.defaultLocale) continue; // Пропускаем язык по умолчанию
      
      if (currentPath === `/${locale}` ||
          currentPath.endsWith(`/${locale}`) ||
          currentPath.startsWith(`/${locale}/`)) {
        return locale;
      }
    }
    
    // Если никакая локаль не найдена в URL, возвращаем язык по умолчанию
    return localeConfig.defaultLocale;
  };

  const currentLang = getCurrentLang();

  return (
    <div className={`language-switcher ${className}`}>
      {localeConfig.availableLocales.map((locale, index) => (
        <React.Fragment key={locale}>
          {index > 0 && <span className='separator'>|</span>}
          <button
            onClick={() => changeLanguage(locale)}
            className={`lang-btn ${currentLang === locale ? 'active' : ''}`}
          >
            {localeConfig.localeLabels[locale] || locale.toUpperCase()}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
};

export { LanguageSwitcher };
